/**
 * InfinitSales - Agente ligado/desligado por canal do inbox (2026-10-14)
 *
 * Bloco "Canais" da tela Agentes (flag agent_channel_pause). Lista só os
 * canais que o workspace tem conectado (GET /users/me/entry-connections, que
 * já cobre os 5) com um interruptor "Agente responde neste canal" por canal.
 * A lista é da EMPRESA: admin e dono gravam (PUT /users/me/agent-channels);
 * membro só lê. Pausado é pausado: vale também para o contato transferido ao
 * agente (a transferência avisa).
 *
 * Caso Bigens de 14/10: o GHL ainda responde o Instagram enquanto o widget
 * já é atendido pela InfinitSales; sem este bloco a saída era zerar o agente
 * principal, o que apaga a configuração da tela.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Radio } from 'lucide-react';
import authService from '../../services/global/authService';
import { labelOf } from '../../utils/channels';

const CHANNEL_ORDER = ['wa_personal', 'wa_business', 'instagram', 'messenger', 'widget'];

const AgentChannelsPanel = ({ colors, paused = [], onChange, readOnly = false, styles = {} }) => {
  const [connections, setConnections] = useState(null);
  const [savingChannel, setSavingChannel] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    authService.listEntryConnections()
      .then((list) => { if (alive) setConnections(Array.isArray(list) ? list : []); })
      .catch(() => { if (alive) setConnections([]); });
    return () => { alive = false; };
  }, []);

  // Um item por canal conectado (o QR vem um por membro; aqui interessa o canal).
  const channels = useMemo(() => {
    const seen = new Map();
    (connections || []).forEach((c) => {
      if (!c?.channel) return;
      if (c.channel === 'wa_personal' && !c.has_line) return;
      const cur = seen.get(c.channel) || { code: c.channel, connected: false, count: 0 };
      cur.count += 1;
      cur.connected = cur.connected || !!c.connected;
      seen.set(c.channel, cur);
    });
    return CHANNEL_ORDER.filter((code) => seen.has(code)).map((code) => seen.get(code));
  }, [connections]);

  const pausedSet = useMemo(() => new Set((paused || []).map((c) => String(c))), [paused]);

  const toggle = async (code) => {
    if (readOnly || savingChannel) return;
    const next = new Set(pausedSet);
    if (next.has(code)) next.delete(code); else next.add(code);
    const list = Array.from(next);
    setSavingChannel(code);
    setError('');
    try {
      const res = await authService.updateAgentChannels(list);
      onChange?.(res?.data?.agent_channels_paused || list);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError((detail && detail.message) || 'Não foi possível salvar. Tente de novo.');
    } finally {
      setSavingChannel(null);
    }
  };

  const s = {
    row: styles.toggleRow || {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '16px 20px', backgroundColor: colors.bgSecondary, borderRadius: '12px', marginBottom: '8px',
    },
    title: styles.toggleTitle || { fontSize: '15px', fontWeight: 600, color: colors.textPrimary },
    desc: styles.toggleDesc || { fontSize: '12px', color: colors.textSecondary, lineHeight: 1.4 },
    switchBase: (on) => ({
      width: '42px', height: '24px', borderRadius: '12px', padding: '2px', border: 'none', flexShrink: 0,
      cursor: readOnly ? 'not-allowed' : 'pointer', transition: 'background-color 0.2s',
      backgroundColor: on ? colors.accentPrimary : colors.borderColor, opacity: readOnly ? 0.6 : 1,
    }),
    knob: (on) => ({
      width: '20px', height: '20px', borderRadius: '50%', backgroundColor: 'white',
      transform: on ? 'translateX(18px)' : 'translateX(0)', transition: 'transform 0.2s',
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    }),
    line: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
      padding: '8px 0', borderTop: `1px solid ${colors.borderColor}`,
    },
  };

  return (
    <div style={{ ...s.row, flexDirection: 'column', alignItems: 'stretch', gap: '6px' }} data-testid="agent-channels-panel">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '6px' }}>
        <span style={s.title}>
          <Radio size={15} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
          Canais
        </span>
        <span style={s.desc}>
          Em qual inbox o agente responde. Desligar um canal cala o agente ali, inclusive para
          contatos transferidos a ele. As outras regras (horário, contato, bloqueio) continuam valendo.
          {readOnly ? ' Só o administrador da empresa altera.' : ''}
        </span>
      </div>
      {connections === null ? (
        <span style={s.desc}>Carregando canais…</span>
      ) : channels.length === 0 ? (
        <span style={s.desc}>Nenhum canal conectado ainda. Conecte um número ou uma conta em Conexões.</span>
      ) : channels.map((ch) => {
        const on = !pausedSet.has(ch.code);
        const busy = savingChannel === ch.code;
        return (
          <div key={ch.code} style={s.line}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                {labelOf(ch.code)}
                {ch.count > 1 ? ` (${ch.count})` : ''}
              </span>
              <span style={{ fontSize: '11.5px', color: on ? colors.textSecondary : '#d97706' }}>
                {on ? 'Agente responde neste canal' : 'Agente pausado neste canal'}
                {!ch.connected ? ' · desconectado' : ''}
              </span>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={on}
              aria-label={`Agente responde no ${labelOf(ch.code)}`}
              disabled={readOnly || !!savingChannel}
              onClick={() => toggle(ch.code)}
              style={{ ...s.switchBase(on), opacity: busy ? 0.6 : s.switchBase(on).opacity }}
            >
              <div style={s.knob(on)} />
            </button>
          </div>
        );
      })}
      {error && <span style={{ fontSize: '12px', color: '#dc2626' }}>{error}</span>}
    </div>
  );
};

export default AgentChannelsPanel;
